<template>
  <div class="franchise-sync-compare">
      <div v-if="loading && !summary && !emptyMessage" class="loading-row">
        <i class="fas fa-spinner fa-spin"></i> Loading FC reconciliation...
      </div>

      <div v-else-if="error" class="error-row">
        <i class="fas fa-exclamation-triangle"></i> {{ error }}
      </div>

      <template v-else>
      <div class="compare-header">
        <div>
          <h3>FranConnect vs ADI Reconciliation</h3>
          <p class="compare-subtitle">
            Roster reconciliation (scenarios 1.1–3.2): FranConnect/JMFS defines
            which locations exist and whether they are Open or Closed.
            <strong>1</strong> — in FranConnect, not in ADI (add to ADI).
            <strong>2</strong> — in ADI, not in FranConnect (close/archive in ADI).
            <strong>3</strong> — in both, Open/Closed differs (update ADI to match FranConnect).
            Compliance status (certificates, pass/fail) is source-of-truth in ADI
            once the roster is synced.
            Filter by scenario, then Export CSV — that file is the full roster
            (1.1–3.2 plus already aligned), not only the open tab.
            <span v-if="sourceLabel" class="source-tag">{{ sourceLabel }}</span>
          </p>
        </div>
        <div class="header-actions">
          <button
            class="btn-secondary"
            :disabled="loading || exporting || source === 'none' || !filteredItems.length"
            @click="exportCsv"
            :title="exportHint"
          >
            <i class="fas fa-download"></i>
            Export CSV
            <span v-if="filteredItems.length" class="export-count">{{ filteredItems.length }}</span>
          </button>
          <button
            class="btn-secondary"
            :disabled="loading || liveLoading"
            title="Calls FranConnect live — requires credentials"
            @click="refreshLive"
          >
            <i class="fas fa-cloud" :class="{ 'fa-spin': liveLoading }"></i>
            Live compare
          </button>
          <button class="btn-primary" :disabled="loading" @click="loadCompare">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </button>
          <button
            v-if="applyableCount > 0"
            class="btn-secondary"
            :disabled="loading || applyLoading"
            title="Preview ADI Open/Closed writes for 2.1, 3.1, and 3.2"
            @click="previewRosterActions"
          >
            <i class="fas" :class="applyLoading ? 'fa-spinner fa-spin' : 'fa-check-double'"></i>
            Preview roster fixes
            <span class="export-count">{{ applyableCount }}</span>
          </button>
        </div>
      </div>

      <div v-if="applyError && !applyPreview" class="apply-error-banner">
        <i class="fas fa-exclamation-triangle"></i>
        {{ applyError }}
      </div>

      <div v-if="applyPreview" ref="applyPanelEl" class="apply-panel">
        <div class="apply-header">
          <h4>
            {{ applyPreview.dry_run ? 'Preview roster fixes' : 'Roster fixes applied' }}
          </h4>
          <p>
            Updates ADI Open/Closed only (2.1 close extras, 3.1 close, 3.2 open).
            Does not create missing stores and does not write FranConnect.
          </p>
        </div>
        <div class="apply-counts">
          <span><strong>{{ applyPreview.counts?.planned || applyPreview.counts?.applied || 0 }}</strong> {{ applyPreview.dry_run ? 'will change' : 'changed' }}</span>
          <span v-if="applyPreview.counts?.skipped">{{ applyPreview.counts.skipped }} skipped</span>
          <span v-if="applyPreview.counts?.unchanged">{{ applyPreview.counts.unchanged }} already correct</span>
          <span v-for="(count, code) in applyPreview.counts?.by_scenario || {}" :key="code">
            {{ code }}: {{ count }}
          </span>
        </div>
        <div class="apply-actions">
          <button class="btn-secondary" :disabled="applyLoading" @click="downloadRosterCsv">
            <i class="fas fa-download"></i>
            Download CSV
          </button>
          <button
            v-if="canApplyRoster && applyPreview.dry_run && (applyPreview.counts?.planned || 0) > 0"
            class="btn-primary"
            :disabled="applyLoading"
            @click="confirmApplyRosterActions"
          >
            <i v-if="applyLoading" class="fas fa-spinner fa-spin"></i>
            Apply {{ applyPreview.counts.planned }} fixes
          </button>
          <button class="btn-secondary" :disabled="applyLoading" @click="applyPreview = null">
            Dismiss
          </button>
        </div>
        <p v-if="applyError" class="apply-error">{{ applyError }}</p>
      </div>

      <div v-if="emptyMessage" class="empty-banner">
        <i class="fas fa-info-circle"></i>
        <div>
          <p>{{ emptyMessage }}</p>
          <p v-if="syncError" class="sync-error-detail">Last sync error: {{ syncError }}</p>
        </div>
      </div>

      <div v-if="summary" class="summary-grid">
        <div class="summary-card">
          <span class="summary-value">{{ summary.fc_sync_filter_match ?? '—' }}</span>
          <span class="summary-label">FC sync filter units</span>
        </div>
        <div class="summary-card" :title="'ComplianceSubject count (not unique store numbers)'">
          <span class="summary-value">{{ summary.db_active_subjects ?? summary.db_active_stores ?? '—' }}</span>
          <span class="summary-label">DB active subjects</span>
        </div>
        <div class="summary-card">
          <span class="summary-value">{{ summary.matched ?? '—' }}</span>
          <span class="summary-label">Matched (by franchisee #)</span>
        </div>
        <div class="summary-card summary-warn">
          <span class="summary-value">{{ missingTotal }}</span>
          <span class="summary-label">In FC filter, not DB</span>
        </div>
        <div class="summary-card">
          <span class="summary-value">{{ extraTotal }}</span>
          <span class="summary-label">In DB, not FC filter</span>
        </div>
      </div>

      <div v-if="inventory" class="inventory-panel">
        <div class="inventory-header">
          <h4>Open stores</h4>
          <p>
            FranConnect decides which stores should be Open. ADI should match that list.
            Certificates and pass/fail are a separate question — this block is roster only.
          </p>
        </div>
        <div class="inventory-compare">
          <div class="inventory-item inventory-emphasis">
            <span class="inventory-kicker">Should be Open</span>
            <span class="inventory-value">{{ fcOpenCount }}</span>
            <span class="inventory-label">FranConnect, last sync</span>
          </div>
          <div class="inventory-item" :class="{ 'inventory-warn': openStoreOff }">
            <span class="inventory-kicker">Are Open in ADI</span>
            <span class="inventory-value">{{ cvOpenSyncedCount || '—' }}</span>
            <span class="inventory-label">Stores marked Open here</span>
          </div>
        </div>
        <p v-if="openStoreOff" class="inventory-aside">
          Off by {{ openStoreGap }} vs FranConnect. The chips below are that gap.
        </p>
        <p class="inventory-aside">
          {{ inDevelopmentCount }} FranConnect stores are In Development — not in the Open target yet.
        </p>
      </div>

      <div v-if="scenarioBreakdown && Object.keys(scenarioBreakdown).length" class="reason-breakdown">
        <button
          v-for="(count, code) in scenarioBreakdown"
          :key="code"
          type="button"
          class="reason-chip"
          :class="[`scenario-${scenarioChipClass(code)}`, { active: scenarioFilter === code }]"
          @click="toggleScenarioFilter(code)"
        >
          {{ scenarioChipLabel(code) }}
          <strong>{{ count }}</strong>
        </button>
      </div>

      <div class="section-tabs">
        <button
          type="button"
          class="section-tab"
          :class="{ active: section === 'all' }"
          @click="setSection('all')"
        >
          All locations
          <span v-if="allTotal" class="tab-count tab-count-muted">{{ allTotal }}</span>
        </button>
        <button
          type="button"
          class="section-tab"
          :class="{ active: section === 'missing_in_db' }"
          @click="setSection('missing_in_db')"
        >
          1 &amp; 3 — Needs action
          <span v-if="missingTotal" class="tab-count">{{ missingTotal }}</span>
        </button>
        <button
          type="button"
          class="section-tab"
          :class="{ active: section === 'extra_in_db' }"
          @click="setSection('extra_in_db')"
        >
          2 — Missing in FranConnect
          <span v-if="extraTotal" class="tab-count tab-count-muted">{{ extraTotal }}</span>
        </button>
      </div>

      <div class="toolbar">
        <input
          v-model="searchQuery"
          type="search"
          placeholder="Filter by store #, name, scenario..."
          @input="debouncedFilter"
        />
        <select v-model="scenarioFilter" @change="applyClientFilter">
          <option value="">All scenarios</option>
          <option value="1.1">1.1 — In FranConnect as Open, not in ADI</option>
          <option value="1.2">1.2 — In FranConnect as Closed, not in ADI</option>
          <option value="2.1">2.1 — In ADI as Open, not in FranConnect</option>
          <option value="2.2">2.2 — In ADI as Closed, not in FranConnect</option>
          <option value="3.1">3.1 — ADI Open, FranConnect Closed</option>
          <option value="3.2">3.2 — ADI Closed, FranConnect Open</option>
          <option value="aligned">Already aligned</option>
          <option value="review">Needs review</option>
        </select>
        <select v-if="section !== 'extra_in_db'" v-model="pathFilter" @change="applyClientFilter">
          <option value="">All sync paths</option>
          <option value="active_or_transfer">Active / Transfer</option>
          <option value="in_development">In Development</option>
        </select>
      </div>

      <div v-if="loading && items.length === 0" class="loading-row">
        <i class="fas fa-spinner fa-spin"></i> Loading rows...
      </div>

      <div v-else-if="filteredItems.length === 0" class="empty-row">
        <i class="fas fa-check-circle"></i>
        <p v-if="emptyMessage">Nothing to review until a sync snapshot is available.</p>
        <p v-else>No rows match this filter.</p>
      </div>

      <div v-else class="table-wrap">
        <table class="compare-table">
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Franchisee #</th>
              <th>Store #</th>
              <th>Name</th>
              <th>FranConnect</th>
              <th>ADI</th>
              <th>What this means</th>
              <th>Recommended action</th>
              <th></th>
              <th v-if="canReview">Review</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedItems" :key="rowKey(row)">
              <td>
                <span
                  class="reason-badge"
                  :class="`scenario-${scenarioChipClass(row.scenario)}`"
                >
                  {{ row.scenario || '—' }}
                </span>
              </td>
              <td class="mono">{{ row.franchisee_no }}</td>
              <td>{{ row.center_name || '—' }}</td>
              <td>{{ row.franchisee_name || row.entity_name || '—' }}</td>
              <td>{{ row.fc_location_status || jmStatusLabel(row.sky_status_label || row.status) }}</td>
              <td>{{ row.db_location_status || jmDbStatus(row) }}</td>
              <td class="reason-cell">
                <div
                  class="reason-detail explanation-text"
                  :title="row.explanation || row.reason_detail"
                >
                  {{ truncate(row.explanation || row.reason_detail || row.scenario_label, 160) }}
                </div>
              </td>
              <td class="reason-cell">
                <div class="action-text">
                  {{ row.recommended_action || '—' }}
                </div>
              </td>
              <td>
                <router-link
                  v-if="row.subject_id"
                  :to="entityLink(row.subject_id)"
                  class="row-link"
                >
                  Record →
                </router-link>
              </td>
              <td v-if="canReview" class="actions-cell">
                <button
                  v-if="row.scenario && row.scenario !== 'aligned'"
                  class="btn-mini"
                  :disabled="actionLoading === row.franchisee_no"
                  @click="acknowledgeGap(row)"
                >
                  Acknowledge
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="filteredItems.length > pageSize" class="pagination">
        <button :disabled="page === 0" @click="page -= 1">Previous</button>
        <span>
          Page {{ page + 1 }} of {{ totalPages }}
          · {{ filteredItems.length }} row(s)
        </span>
        <button :disabled="page >= totalPages - 1" @click="page += 1">Next</button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { debounce } from 'lodash-es'
import api from '@/services/api'
import auth from '@/services/auth'

const props = defineProps({
  projectId: { type: Number, required: true },
})

const emit = defineEmits(['updated'])

const loading = ref(false)
const liveLoading = ref(false)
const exporting = ref(false)
const error = ref(null)
const emptyMessage = ref(null)
const syncError = ref(null)
const section = ref('all')
const summary = ref(null)
const source = ref('snapshot')
const items = ref([])
const searchQuery = ref('')
const pathFilter = ref('')
const scenarioFilter = ref('')
const page = ref(0)
const pageSize = 50
const actionLoading = ref(null)
const useLive = ref(false)
const applyLoading = ref(false)
const applyPreview = ref(null)
const applyError = ref(null)
const applyPanelEl = ref(null)

const inventory = computed(() => summary.value?.inventory || null)
const fcOpenCount = computed(() => (
  inventory.value?.fc_open_store_numbers_at_sync
  ?? inventory.value?.open_store_numbers
  ?? '—'
))
const inDevelopmentCount = computed(() => (
  inventory.value?.fc_in_development_store_numbers_at_sync
  ?? inventory.value?.fc_in_development_store_numbers
  ?? '—'
))
const cvOpenSyncedCount = computed(() => inventory.value?.cv_open_store_numbers_synced ?? 0)
const fcOpenNumeric = computed(() => {
  const raw = fcOpenCount.value
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
})
const openStoreOff = computed(() => (
  fcOpenNumeric.value !== null && cvOpenSyncedCount.value !== fcOpenNumeric.value
))
const openStoreGap = computed(() => {
  if (fcOpenNumeric.value === null) return '—'
  return Math.abs(cvOpenSyncedCount.value - fcOpenNumeric.value)
})

const allTotal = computed(() => items.value.length)

const extraTotal = computed(() => (
  items.value.filter((row) => String(row.scenario || '').startsWith('2')).length
))

const missingTotal = computed(() => (
  items.value.filter((row) => {
    const code = String(row.scenario || '')
    return code !== 'aligned' && !code.startsWith('2')
  }).length
))

const sectionItems = computed(() => {
  const rows = items.value
  if (section.value === 'all') return rows
  if (section.value === 'extra_in_db') {
    return rows.filter((row) => String(row.scenario || '').startsWith('2'))
  }
  return rows.filter((row) => {
    const code = String(row.scenario || '')
    return code !== 'aligned' && !code.startsWith('2')
  })
})

const exportHint = computed(() => (
  'Exports the rows matching the current tab, search, and scenario filters. '
  + 'Open All locations with no filter for the full reconciliation file.'
))

const SCENARIO_LABELS = {
  '1.1': '1.1 — In FranConnect as Open, not in ADI',
  '1.2': '1.2 — In FranConnect as Closed, not in ADI',
  '2.1': '2.1 — In ADI as Open, not in FranConnect',
  '2.2': '2.2 — In ADI as Closed, not in FranConnect',
  '3.1': '3.1 — ADI Open, FranConnect Closed',
  '3.2': '3.2 — ADI Closed, FranConnect Open',
  aligned: 'Already aligned',
  review: 'Needs review',
}

const scenarioBreakdown = computed(() => summary.value?.scenario_breakdown || null)

const canReview = computed(() => auth.hasPlatformAccess() || auth.hasCrossOrgComplianceAccess())
const canApplyRoster = computed(() => (
  canReview.value
  || auth.hasAnyRole(['ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN'])
))

const APPLYABLE_SCENARIOS = new Set(['2.1', '3.1', '3.2'])

const applyableRows = computed(() => (
  items.value.filter((row) => APPLYABLE_SCENARIOS.has(String(row.scenario || '')))
))

const applyableCount = computed(() => {
  const breakdown = scenarioBreakdown.value || {}
  const fromSummary = (breakdown['2.1'] || 0) + (breakdown['3.1'] || 0) + (breakdown['3.2'] || 0)
  return fromSummary || applyableRows.value.length
})

const sourceLabel = computed(() => {
  if (source.value === 'live') return 'Live FC API'
  if (source.value === 'none') return 'No snapshot'
  return 'From last sync snapshot'
})

const filteredItems = computed(() => {
  let rows = sectionItems.value
  const q = searchQuery.value.trim().toLowerCase()
  if (q) {
    rows = rows.filter((row) => {
      const haystack = [
        row.franchisee_no,
        row.center_name,
        row.franchisee_name,
        row.entity_name,
        row.compliance_status,
        row.reason_code,
        row.reason_label,
        row.reason_detail,
        row.explanation,
        row.recommended_action,
        row.scenario,
        row.scenario_label,
        row.sky_status_label,
        row.fc_location_status,
        row.db_location_status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }
  if (section.value !== 'extra_in_db' && pathFilter.value) {
    rows = rows.filter((row) => row.sync_path === pathFilter.value)
  }
  if (scenarioFilter.value) {
    rows = rows.filter((row) => row.scenario === scenarioFilter.value)
  }
  return rows
})

function scenarioChipLabel(code) {
  return SCENARIO_LABELS[code] || code || '—'
}

function scenarioChipClass(code) {
  if (!code) return 'review'
  if (String(code).startsWith('1')) return 'one'
  if (String(code).startsWith('2')) return 'two'
  if (String(code).startsWith('3')) return 'three'
  return String(code)
}

function jmStatusLabel(status) {
  const text = String(status || '').toLowerCase()
  if (text === '1' || text === 'active' || text.includes('open')) return 'Open'
  if (
    text === '0'
    || text === 'terminated'
    || text.includes('closed')
    || text === 'inactive'
    || text === 'archived'
  ) {
    return 'Closed'
  }
  return status || '—'
}

function jmDbStatus(row) {
  if (row.db_location_status) return row.db_location_status
  const status = String(row.compliance_status || '').toUpperCase()
  if (!status) return 'Not in ADI'
  if (status === 'INACTIVE' || status === 'ARCHIVED') return 'Closed'
  return 'Open'
}

function truncate(text, max) {
  const s = String(text || '')
  return s.length > max ? `${s.slice(0, max)}…` : s
}

function toggleScenarioFilter(code) {
  scenarioFilter.value = scenarioFilter.value === code ? '' : code
  page.value = 0
  if (!scenarioFilter.value) return
  const selected = String(scenarioFilter.value)
  if (selected === 'aligned' && section.value !== 'all') {
    section.value = 'all'
  } else if (selected.startsWith('2') && section.value === 'missing_in_db') {
    section.value = 'extra_in_db'
  } else if (
    section.value === 'extra_in_db'
    && (selected.startsWith('1') || selected.startsWith('3') || selected === 'review')
  ) {
    section.value = 'missing_in_db'
  }
}

const totalPages = computed(() => Math.max(1, Math.ceil(filteredItems.value.length / pageSize)))

const pagedItems = computed(() => {
  const start = page.value * pageSize
  return filteredItems.value.slice(start, start + pageSize)
})

function rowKey(row) {
  return `${section.value}:${row.franchisee_no}`
}

function entityLink(subjectId) {
  return `/projects/${props.projectId}/entities/${subjectId}`
}

function setSection(next) {
  section.value = next
  page.value = 0
}

async function fetchAllItems(sectionName, live = false) {
  const pageLimit = 500
  let offset = 0
  let allItems = []
  let hasMore = true
  let lastSummary = null
  let lastSource = 'snapshot'
  let message = null
  let lastSyncError = null

  while (hasMore) {
    const params = new URLSearchParams({
      section: sectionName,
      limit: String(pageLimit),
      offset: String(offset),
    })
    if (live) params.append('live', 'true')

    const response = await api.get(
      `/franconnect/projects/${props.projectId}/sync-compare/?${params}`,
    )
    const payload = response.data || {}
    if (payload.success === false) {
      throw new Error(payload.error || payload.message || 'Reconciliation unavailable')
    }
    const data = payload.data || {}
    lastSummary = data.summary || lastSummary
    lastSource = data.source || lastSource
    message = data.message || message
    lastSyncError = data.sync_error || lastSyncError
    allItems = allItems.concat(data.items || [])
    hasMore = Boolean(data.pagination?.has_more)
    offset += pageLimit
    if (offset > 20000 || lastSource === 'none') break
  }

  return {
    items: allItems,
    summary: lastSummary,
    source: lastSource,
    total: allItems.length,
    message,
    sync_error: lastSyncError,
  }
}

async function loadCompare() {
  loading.value = true
  error.value = null
  emptyMessage.value = null
  syncError.value = null
  try {
    const data = await fetchAllItems('all', useLive.value)
    summary.value = data.summary || null
    source.value = data.source || 'snapshot'
    items.value = data.items || []
    emptyMessage.value = data.message || null
    syncError.value = data.sync_error || null
  } catch (err) {
    error.value = err.response?.data?.error
      || err.response?.data?.message
      || err.message
      || 'Failed to load reconciliation'
  } finally {
    loading.value = false
    useLive.value = false
  }
}

async function refreshLive() {
  useLive.value = true
  liveLoading.value = true
  try {
    await loadCompare()
  } finally {
    liveLoading.value = false
  }
}

async function exportCsv() {
  exporting.value = true
  try {
    const rows = filteredItems.value
    if (!rows.length) return
    const csv = [
      'scenario',
      'scenario_label',
      'franchisee_no',
      'store_number',
      'name',
      'franconnect_status',
      'co_verify_status',
      'recommended_action',
      'explanation',
      'is_store',
      'is_franchisee',
      'sync_path',
      'subject_id',
    ].join(',') + '\n'
      + rows.map((r) => [
        r.scenario,
        csvEscape(r.scenario_label),
        r.franchisee_no,
        csvEscape(r.center_name),
        csvEscape(r.franchisee_name || r.entity_name),
        csvEscape(r.fc_location_status || jmStatusLabel(r.sky_status_label || r.status)),
        csvEscape(r.db_location_status || jmDbStatus(r)),
        csvEscape(r.recommended_action),
        csvEscape(r.explanation || r.reason_detail),
        r.is_store,
        r.is_franchisee,
        r.sync_path,
        r.subject_id,
      ].join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const scope = section.value === 'all' && !searchQuery.value && !scenarioFilter.value && !pathFilter.value
      ? 'all'
      : `filtered-${section.value}`
    link.download = `fc-reconciliation-scenarios-${scope}-${props.projectId}.csv`
    link.click()
    URL.revokeObjectURL(url)
  } finally {
    exporting.value = false
  }
}

function csvEscape(value) {
  const str = String(value || '')
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

function rosterActionError(err) {
  const data = err.response?.data
  if (data && typeof data === 'object' && !(data instanceof Blob) && data.error) {
    return data.error
  }
  const status = err.response?.status
  if (status === 504 || status === 503) {
    return 'Roster request timed out. Preview uses the rows already on this page; try Apply again.'
  }
  return err.message || 'Roster action failed'
}

function buildLocalRosterPreview() {
  const byScenario = {}
  const rows = applyableRows.value.map((row) => {
    const scenario = String(row.scenario || '')
    byScenario[scenario] = (byScenario[scenario] || 0) + 1
    return {
      scenario,
      franchisee_no: row.franchisee_no || '',
      store_number: row.center_name || '',
      name: row.franchisee_name || row.entity_name || '',
      fc_status: row.fc_location_status || '',
      adi_status: row.db_location_status || '',
      target_adi_status: scenario === '3.2' ? 'Open' : 'Closed',
      recommended_action: row.recommended_action || '',
      subject_id: row.subject_id || null,
      result: 'planned',
      notes: '',
    }
  })
  return {
    dry_run: true,
    source: source.value,
    counts: {
      eligible: rows.length,
      planned: rows.length,
      applied: 0,
      skipped: 0,
      unchanged: 0,
      by_scenario: byScenario,
    },
    rows,
  }
}

async function runRosterActions({ dryRun }) {
  applyLoading.value = true
  applyError.value = null
  try {
    const response = await api.post(
      `/franconnect/projects/${props.projectId}/apply-roster-actions/`,
      {
        dry_run: dryRun,
        live: useLive.value,
      },
      { timeout: 120000 },
    )
    applyPreview.value = response.data?.data || null
    if (!applyPreview.value) {
      applyError.value = 'Preview returned no data. Refresh Reconciliation and try again.'
    }
    if (!dryRun) {
      emit('updated')
      await loadCompare()
    }
  } catch (err) {
    applyError.value = rosterActionError(err)
  } finally {
    applyLoading.value = false
  }
}

function previewRosterActions() {
  applyError.value = null
  const preview = buildLocalRosterPreview()
  if (!preview.rows.length) {
    applyError.value = 'No 2.1 / 3.1 / 3.2 rows are loaded. Click Refresh, then try again.'
    applyPreview.value = null
    return
  }
  applyPreview.value = preview
  nextTick(() => {
    applyPanelEl.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  })
}

function downloadRosterCsv() {
  const rows = applyPreview.value?.rows || []
  if (!rows.length) return
  const headers = [
    'scenario',
    'franchisee_no',
    'store_number',
    'name',
    'fc_status',
    'adi_status',
    'target_adi_status',
    'recommended_action',
    'result',
    'notes',
  ]
  const lines = [
    headers.join(','),
    ...rows.map((row) => headers.map((key) => csvEscape(row[key])).join(',')),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const suffix = applyPreview.value?.dry_run === false ? 'applied' : 'preview'
  link.download = `fc-roster-actions-${suffix}-${props.projectId}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

function confirmApplyRosterActions() {
  const planned = applyPreview.value?.counts?.planned || 0
  const ok = window.confirm(
    `Apply ${planned} ADI Open/Closed fix(es)?\n\n`
    + '2.1 / 3.1 close locations in ADI. 3.2 opens them. FranConnect is not changed.',
  )
  if (!ok) return
  runRosterActions({ dryRun: false })
}

async function acknowledgeGap(row) {
  const prefix = String(row.scenario || '').startsWith('2') ? 'gap_db' : 'gap_fc'
  actionLoading.value = row.franchisee_no
  try {
    await api.patch('/franconnect/sync-issues/', {
      project_id: props.projectId,
      issue_key: `${prefix}:${row.franchisee_no}`,
      status: 'acknowledged',
    })
    emit('updated')
  } catch (err) {
    error.value = err.response?.data?.error || 'Acknowledge failed'
  } finally {
    actionLoading.value = null
  }
}

function applyClientFilter() {
  page.value = 0
}

const debouncedFilter = debounce(() => {
  page.value = 0
}, 300)

watch(() => props.projectId, () => {
  page.value = 0
  loadCompare()
})

onMounted(loadCompare)

defineExpose({ refresh: loadCompare })
</script>

<style scoped>
.compare-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.compare-header h3 {
  margin: 0 0 0.35rem;
  font-size: 1.1rem;
  color: #1e293b;
}

.compare-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 0.875rem;
  max-width: 640px;
}

.empty-banner {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fbbf24;
  color: #92400e;
}

.empty-banner p {
  margin: 0 0 0.35rem;
}

.sync-error-detail {
  font-size: 0.8rem;
  color: #b45309;
  word-break: break-word;
}

.source-tag {
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: #f1f5f9;
  font-size: 0.75rem;
  color: #475569;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.apply-panel {
  margin: 0 0 1rem;
  padding: 1rem 1.1rem;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #eff6ff;
}

.apply-header h4 {
  margin: 0 0 0.25rem;
  color: #1e3a8a;
}

.apply-header p {
  margin: 0 0 0.75rem;
  color: #334155;
  font-size: 0.85rem;
}

.apply-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  color: #1e293b;
}

.apply-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.apply-error-banner {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  margin: 0 0 1rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.apply-error {
  margin: 0.75rem 0 0;
  color: #b91c1c;
  font-size: 0.85rem;
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background: #1e40af;
  color: white;
}

.btn-secondary {
  background: white;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.summary-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.85rem;
  text-align: center;
}

.summary-warn {
  border-color: #fbbf24;
  background: #fffbeb;
}

.summary-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
}

.summary-label {
  font-size: 0.72rem;
  color: #64748b;
}

.inventory-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1.1rem;
  margin-bottom: 1.25rem;
}

.inventory-header h4 {
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
  color: #0f172a;
}

.inventory-header p {
  margin: 0 0 0.85rem;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;
}

.inventory-compare {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
}

.inventory-kicker {
  display: block;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  margin-bottom: 0.15rem;
}

.inventory-aside {
  margin: 0.65rem 0 0;
  font-size: 0.78rem;
  color: #64748b;
}

.inventory-warn {
  border-color: #f59e0b;
  background: #fffbeb;
}

.inventory-item {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.7rem 0.75rem;
}

.inventory-emphasis {
  border-color: #93c5fd;
  background: #eff6ff;
}

.inventory-value {
  display: block;
  font-size: 1.35rem;
  font-weight: 700;
  color: #1e293b;
}

.inventory-label {
  display: block;
  font-size: 0.72rem;
  color: #64748b;
  margin-top: 0.15rem;
}

.inventory-hint {
  display: block;
  font-size: 0.65rem;
  color: #94a3b8;
  margin-top: 0.2rem;
}

.section-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.section-tab {
  padding: 0.55rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #64748b;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.section-tab.active {
  border-color: #1e40af;
  color: #1e40af;
  background: #eff6ff;
}

.tab-count {
  background: #dc2626;
  color: white;
  font-size: 0.7rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
}

.tab-count-muted {
  background: #94a3b8;
}

.export-count {
  background: #1e40af;
  color: white;
  font-size: 0.7rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.toolbar input,
.toolbar select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  min-width: 220px;
}

.table-wrap {
  overflow-x: auto;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.compare-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.compare-table th,
.compare-table td {
  padding: 0.65rem 0.85rem;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
}

.compare-table th {
  background: #f8fafc;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
}

.mono {
  font-family: ui-monospace, monospace;
  font-weight: 600;
}

.path-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.path-active_or_transfer {
  background: #dcfce7;
  color: #166534;
}

.path-in_development {
  background: #dbeafe;
  color: #1e40af;
}

.reason-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.reason-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: white;
  font-size: 0.75rem;
  cursor: pointer;
  color: #334155;
}

.reason-chip.active {
  border-color: #1e40af;
  background: #eff6ff;
  color: #1e40af;
  font-weight: 600;
}

.reason-cell {
  max-width: 360px;
}

.reason-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}

.reason-never_synced { background: #fee2e2; color: #991b1b; }
.reason-inactive_in_db { background: #fef3c7; color: #92400e; }
.reason-external_id_mismatch { background: #e0e7ff; color: #3730a3; }
.reason-archived_in_db { background: #f1f5f9; color: #475569; }
.reason-duplicate_store { background: #ffedd5; color: #9a3412; }
.reason-upsert_error,
.reason-location_conflict { background: #fee2e2; color: #b91c1c; }
.reason-no_store_number { background: #e0e7ff; color: #3730a3; }

.scenario-one { background: #dbeafe; color: #1e40af; }
.scenario-two { background: #dcfce7; color: #166534; }
.scenario-three { background: #ffedd5; color: #9a3412; }
.scenario-aligned { background: #f1f5f9; color: #475569; }
.scenario-review { background: #fce7f3; color: #9d174d; }

.reason-detail {
  margin-top: 0.25rem;
  font-size: 0.72rem;
  color: #64748b;
  line-height: 1.3;
}

.explanation-text,
.action-text {
  margin-top: 0;
  font-size: 0.78rem;
  color: #334155;
  line-height: 1.35;
}

.action-text {
  font-weight: 600;
  color: #1e293b;
}

.row-link {
  color: #1e40af;
  font-weight: 600;
  text-decoration: none;
  font-size: 0.8rem;
}

.actions-cell {
  white-space: nowrap;
}

.btn-mini {
  padding: 0.25rem 0.55rem;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  background: white;
  color: #92400e;
  font-size: 0.75rem;
  cursor: pointer;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
}

.loading-row,
.error-row,
.empty-row {
  text-align: center;
  padding: 2rem;
  color: #64748b;
}
</style>
